import { InjectionToken } from '@angular/core';
import {
  type ActionCreator,
  type ActionReducer,
  createReducer,
  type ReducerTypes,
} from '@ngrx/store';

import { type EngineStateBase } from './engine-store';

/**
 * Fields a library outside `runtime/engine` adds to the `engine` feature slice, together with the
 * handlers that maintain them.
 *
 * The slice is one state object reduced by one `createReducer`, so an editor-time slice cannot
 * simply become a feature of its own: the editor's handlers write the very document fields the
 * runtime's do — undo/redo restores `ids`, `entities` and `connections` in place. An extension is
 * how a `scope:dashboard` library contributes such handlers without any `scope:runtime` library
 * naming the editor.
 *
 * Handlers state the fields they add as optional (`EngineStateBase & Partial<MySlice>`). That is
 * not a formality: a published runtime registers no extension at all, so from the composition's
 * point of view no added field is guaranteed to be there.
 */
export interface EngineStateExtension {
  /** The fields this extension adds, and their values before it has handled anything. */
  readonly initialState: object;
  /**
   * Appended after every handler the engine itself registers, so that for an action both react to,
   * the extension sees the engine's result.
   */
  readonly reducerTypes: readonly ReducerTypes<EngineStateBase, ActionCreator[]>[];
}

/**
 * Multi-provider of {@link EngineStateExtension}.
 *
 * Bind it in the injector that registers the `engine` feature — the root injector, since
 * `StoreModule.forFeature` runs there at bootstrap. An extension provided in a lazily-loaded
 * injector arrives after the feature reducer has been built and would be silently ignored.
 */
export const ENGINE_STATE_EXTENSION = new InjectionToken<readonly EngineStateExtension[]>(
  'Engine state extension',
);

/**
 * Builds the `engine` feature reducer from the engine's own slices plus every registered
 * extension.
 *
 * @param initialBaseState the engine's own fields before any action is handled
 * @param baseReducerTypes the engine's own handlers, in the order they must run
 * @param extensions contributions from outside `runtime/engine`, appended in registration order
 */
export function composeEngineReducer(
  initialBaseState: EngineStateBase,
  baseReducerTypes: readonly ReducerTypes<EngineStateBase, ActionCreator[]>[],
  extensions: readonly EngineStateExtension[],
): ActionReducer<EngineStateBase> {
  const initialState = extensions.reduce<EngineStateBase>(
    (state, extension) => ({ ...state, ...extension.initialState }),
    initialBaseState,
  );

  return createReducer(
    initialState,
    ...baseReducerTypes,
    ...extensions.flatMap((extension) => extension.reducerTypes),
  );
}
